import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { BinaryDependencyConfidence } from '../../../../../../shared/binaryDependencyConfidence';
import { useParams } from '../../../../../contexts/RouterContext';
import useGetReleaseDependencyConfidence from '../../../../../hooks/api/binary/useGetReleaseDependencyConfidence';
import useGetRevisionDependencyConfidence from '../../../../../hooks/api/binary/useGetRevisionDependencyConfidence';
import useGetScope from '../../../../../hooks/useGetScope';
import { getSelectedReleaseId } from '../../../../../redux/project_grouping/selectors';
import Card from '../../../../generic/Card/Card';
import BoldText from '../../../../generic/Text/BoldText';
import type { Dependency } from '../../../../../../shared/dependenciesV2/response';
import type { Props as CardProps } from '../../../../generic/Card/Card';
import type { RevisionRouteParams } from '../../../projectDashboardTypes';

import styles from '../DependencyDrawer.module.less';

type Props = {
  dependency: Pick<Dependency, 'locator'>;
};

type ConfidenceBannerProps = {
  confidence: BinaryDependencyConfidence;
};

const confidenceColorMap: Record<BinaryDependencyConfidence, CardProps['color']> = {
  [BinaryDependencyConfidence.High]: 'green',
  [BinaryDependencyConfidence.Medium]: 'yellow',
  [BinaryDependencyConfidence.Low]: 'pink',
  [BinaryDependencyConfidence.Unknown]: 'blue',
};

/**
 * Exported to make it easier to test.
 */
export const ConfidenceBanner: VoidFunctionComponent<ConfidenceBannerProps> = ({ confidence }) => {
  const confidenceColor = confidenceColorMap[confidence];

  return (
    <Card padding="xs" variant="fill" color={confidenceColor} isFullWidth>
      <Card.Content>
        <span className={styles.confidenceBanner}>
          This package is a <BoldText>{confidence.toLowerCase()} confidence</BoldText>  match for your codebase.
        </span>
      </Card.Content>
    </Card>
  );
};

const ProjectDependencyConfidence: VoidFunctionComponent<Props> = ({ dependency }) => {
  const { revision_id: revisionId, project_id: projectId } = useParams<RevisionRouteParams>();
  const revisionLocator = `${projectId}$${revisionId}`;

  const { data: confidence } = useGetRevisionDependencyConfidence(revisionLocator, dependency.locator, true);

  if (!confidence) {
    return null;
  }

  return (
    <ConfidenceBanner confidence={confidence.confidences[dependency.locator] as BinaryDependencyConfidence} />
  );
};

const ReleaseDependencyConfidence: VoidFunctionComponent<Props> = ({ dependency }) => {
  const releaseId = useSelector(getSelectedReleaseId);

  const { data: confidence } = useGetReleaseDependencyConfidence(releaseId, dependency.locator, true);

  if (!confidence) {
    return null;
  }

  return <ConfidenceBanner confidence={confidence.confidences[dependency.locator] as BinaryDependencyConfidence} />;
};

const BinaryConfidence: VoidFunctionComponent<Props> = ({ dependency }) => {
  const scope = useGetScope();

  if (!scope) {
    return null;
  }

  return (
    <div className={styles.binaryConfidenceContainer}>
      {scope === 'project' ? <ProjectDependencyConfidence dependency={dependency} /> : <ReleaseDependencyConfidence dependency={dependency} />}
    </div>
  );
};

export default BinaryConfidence;
